import { useEffect, useRef, useState } from "react";
import { fetchContributors, addContributor, updateContributor, deleteContributor, formatApiErrorDetail } from "@/villa142/api";
import { ConfirmModal } from "@/villa142/admin/ConfirmModal";

export default function ContributorsView({ reload }) {
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const timers = useRef({});

  const load = async () => setList(await fetchContributors());
  useEffect(() => { load(); }, []);

  if (!list) return <p className="v142-copy">Loading…</p>;

  const refresh = async () => { await load(); reload(); };

  const rename = (cid, value) => {
    clearTimeout(timers.current[cid]);
    timers.current[cid] = setTimeout(async () => {
      await updateContributor(cid, { name: value });
      refresh();
    }, 700);
  };

  const move = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= list.length) return;
    await updateContributor(list[idx].id, { order: t });
    await updateContributor(list[t].id, { order: idx });
    refresh();
  };

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setError("");
    try {
      await addContributor(n);
      setName("");
      refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runConfirmed = async () => {
    setBusy(true);
    setError("");
    try {
      await confirm.action();
      setConfirm(null);
      await refresh();
    } catch (e) {
      setConfirm(null);
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="v142-contributors-view">
      <p className="v142-copy">
        Contributors are the perspectives available on every question. The default responder is pre-selected for new
        reflections; the shared label marks the joint voice. Contributors with responses are archived, never deleted,
        so their reflections and history remain intact.
      </p>
      {error && <p className="v142-error" role="alert" data-testid="v142-contrib-error">{error}</p>}

      <div className="v142-contrib-list">
        {list.map((c, i) => (
          <div className={`v142-contrib-rowitem${c.archived ? " is-archived" : ""}`} key={c.id} data-testid={`v142-contrib-row-${c.id}`}>
            <div className="v142-contrib-main">
              <input
                className="v142-input v142-input-small"
                aria-label="Contributor name"
                defaultValue={c.name}
                disabled={c.archived}
                data-testid={`v142-contrib-name-${c.id}`}
                onChange={(e) => rename(c.id, e.target.value)}
              />
              <span className="v142-entry-meta">
                {c.entry_count} reflection{c.entry_count === 1 ? "" : "s"}
                {c.archived ? " · archived" : ""}
              </span>
            </div>
            <div className="v142-contrib-ctrl">
              <button type="button" className="v142-link" aria-label="Move up" onClick={() => move(i, -1)}>↑</button>
              <button type="button" className="v142-link" aria-label="Move down" onClick={() => move(i, 1)}>↓</button>
              <label className="v142-qe-check">
                <input
                  type="radio"
                  name="v142-default-responder"
                  checked={Boolean(c.is_default)}
                  disabled={c.archived}
                  data-testid={`v142-contrib-default-${c.id}`}
                  onChange={async () => { await updateContributor(c.id, { is_default: true }); refresh(); }}
                />
                Default responder
              </label>
              <label className="v142-qe-check">
                <input
                  type="radio"
                  name="v142-shared-label"
                  checked={Boolean(c.is_shared)}
                  disabled={c.archived}
                  data-testid={`v142-contrib-shared-${c.id}`}
                  onChange={async () => { await updateContributor(c.id, { is_shared: true }); refresh(); }}
                />
                Shared label
              </label>
              {!c.archived ? (
                <button
                  type="button"
                  className="v142-link v142-danger-link"
                  data-testid={`v142-contrib-archive-${c.id}`}
                  onClick={() =>
                    setConfirm({
                      title: `Archive ${c.name}`,
                      body: `${c.name} will no longer appear as a perspective for new reflections. ${c.entry_count ? "Their existing reflections stay visible in the studio." : ""}`,
                      confirmLabel: "Archive",
                      note: "You can restore them at any time.",
                      action: () => updateContributor(c.id, { archived: true }),
                    })
                  }
                >
                  Archive
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="v142-link"
                    data-testid={`v142-contrib-restore-${c.id}`}
                    onClick={async () => { await updateContributor(c.id, { archived: false }); refresh(); }}
                  >
                    Restore
                  </button>
                  {c.entry_count === 0 && (
                    <button
                      type="button"
                      className="v142-link v142-danger-link"
                      data-testid={`v142-contrib-delete-${c.id}`}
                      onClick={() =>
                        setConfirm({
                          title: `Delete ${c.name}`,
                          body: `${c.name} has no reflections and will be removed entirely.`,
                          confirmLabel: "Delete",
                          action: () => deleteContributor(c.id),
                        })
                      }
                    >
                      Delete
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="v142-contrib-add">
        <input
          className="v142-input v142-input-small"
          placeholder="Add a contributor…"
          aria-label="New contributor name"
          value={name}
          data-testid="v142-contrib-add-input"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
        />
        <button type="button" className="v142-btn v142-btn-small" data-testid="v142-contrib-add-btn" onClick={add}>
          Add contributor
        </button>
      </div>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        body={confirm?.body}
        note={confirm?.note}
        confirmLabel={confirm?.confirmLabel || "Delete"}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirmed}
      />
    </div>
  );
}
